import { Container, Graphics, SCALE_MODES, Sprite, Texture } from "pixi.js"
import { ButterfliesConfig, Coordinate, TileSize } from "game-common/models"
import { ScalingParams } from "../../../../client_util"
import { daylightFraction } from "./day_gate"
import { windAt } from "./wind"

// One butterfly. Plain numbers only, like the leaves: the loop mutates these in place and allocates
// nothing per frame.
interface Butterfly {
    sprite: Sprite
    x: number
    y: number
    // Where it is going, and where it has decided to go next. The heading EASES towards the target
    // rather than snapping, so a turn is a curve; re-rolling the target often is what makes the path
    // wander instead of committing to a direction.
    heading: number
    headingTarget: number
    nextTurnMs: number
    speed: number
    // The wingbeat. One phase drives both the wings and the bob, because they are the same event.
    flapPhase: number
    flapRate: number
    bob: number
    scale: number
    baseAlpha: number
    // What it is doing. A butterfly that never stops reads as restless over a long look; real ones
    // spend a good part of their time sitting still, and the contrast between frantic flight and
    // sudden stillness is most of what makes them feel alive rather than animated.
    state: "flying" | "approaching" | "perched"
    // Where it is heading to land, and when its current state runs out.
    perchX: number
    perchY: number
    stateUntilMs: number
}

// Somewhere to land: vegetation near (x, y), or null if there is none. Supplied by the renderer from
// the grass registry; injected rather than reached for so the flight can be tested without one, and so
// a room with no grass simply has butterflies that never settle.
export type PerchFinder = (x: number, y: number, radius: number) => { x: number; y: number } | null

/**
 * Butterflies over a camp by day.
 *
 * WHY THIS IS AN EFFECT AND NOT TILES. A tile can only sit at a multiple of the grid, carries no
 * per-instance rotation or scale, and moving one means a map delta and a re-render -- the most
 * expensive thing the map renderer does. A butterfly needs continuous sub-pixel position, a heading,
 * and a silhouette that changes every few frames. That is a sprite, and the windblown leaves already
 * established the shape: a small pool in world space, culled to a box around the player, gated on the
 * hour, tinted from a palette, drawn with art generated once rather than shipped.
 *
 * WHAT MAKES IT READ AS A BUTTERFLY rather than as a leaf that happens to go upwards. At this size --
 * a wingspan of about seven world pixels -- the FLAP is the whole thing, so the wings fold right down
 * towards the body and spring back about ten times a second. The body bobs off the SAME phase, because
 * a butterfly rises as its wings come down and sinks between beats; flap and bob being one event is
 * what stops it looking like fluttering litter. And the heading never settles: it eases toward a new
 * target every second or so, so the path scallops and wanders.
 *
 * They also shelter from the wind. A butterfly does not fly in a gust, and the gust here is the same
 * shared envelope that carries the leaves and bends the grass (wind.ts) -- so when a gust rolls
 * through, the leaves surge, the grass leans, and the butterflies slow down and sink. That is the
 * difference between three animations and one piece of weather.
 *
 * AND THEY SETTLE. Every so often one picks a tuft of grass nearby, flies to it, folds its wings and
 * sits, opening and closing them slowly, before taking off again. Landing DECELERATES into the perch
 * rather than arriving at cruising speed and stopping dead -- and because the wingbeat is derived from
 * travel speed, slowing down slows the flap on its own, so it settles instead of freezing. Gusts make
 * them both readier to land and slower to leave, which is what sheltering actually looks like.
 *
 * FEW OF THEM, deliberately. Three or four. A butterfly is something you notice; a dozen is a plague
 * of moths.
 *
 * THEY LINGER rather than streaming past. Leaves are carried through the box and recycled at its far
 * edge; a butterfly that reaches the edge is steered back inside instead, because they mill about a
 * meadow rather than crossing it.
 *
 * LIVE TUNING, from the browser console -- no reload:
 *   window.butterflies = true | false    force them on in ANY room at any hour, or off in a camp
 *   window.butterflyCount = 8            how many are about (a room's count, before the daylight scaling)
 *   window.butterflySpeed = 22           cruising speed, world px/sec
 */
export class Butterflies {
    readonly container: Container

    // Above the fog and the grass, below the windblown leaves, well under the sky.
    static readonly Z_INDEX = 898000

    // However many a room asks for. They are cheap, but they are also meant to be rare.
    static readonly MAX_BUTTERFLIES = 24

    private static readonly DEFAULT_COUNT = 3
    // Cruising speed. A butterfly beating ten times a second and barely crossing a tile reads as
    // swimming in treacle -- the eye checks the two against each other and knows. See FLAP_REFERENCE.
    private static readonly DEFAULT_SPEED = 58
    private static readonly DEFAULT_ALPHA = 0.95
    private static readonly DEFAULT_SCALE_MIN = 0.34
    private static readonly DEFAULT_SCALE_MAX = 0.52
    // Cabbage whites and brimstones, mostly: pale colours are what stay legible against grass. The one
    // orange is the occasional fritillary, and it is the one anybody will actually notice.
    private static readonly DEFAULT_COLORS = [0xfdf6e3, 0xfff4b8, 0xf7e7a8, 0xffffff, 0xe8a33d]

    // The travel speed a butterfly's own wingbeat is quoted against. The beat is derived from how fast
    // it is actually going, not set independently, so the two can never drift apart: a butterfly
    // sheltering from a gust slows AND beats more slowly, and a fast one beats harder. Getting this
    // wrong in either direction is immediately obvious and hard to name -- it just looks wrong.
    private static readonly FLAP_REFERENCE = 58

    // The box they are kept in, as a half-extent in world px around the player -- the visible world
    // plus a margin, same as the leaves.
    private static readonly HALF_W = (ScalingParams.viewportX + 2) * TileSize
    private static readonly HALF_H = (ScalingParams.viewportY + 2) * TileSize
    // Inside this fraction of the box they fly freely; past it they are steered back. Kept well inside
    // the edge so the turn happens off-screen and reads as a butterfly changing its mind.
    private static readonly HOME_FRACTION = 0.72

    // A jump bigger than this is a teleport or a room change, not walking.
    // How far it will go looking for somewhere to land. Kept inside its own box, or one would set off
    // across the map towards a tuft you cannot see.
    private static readonly PERCH_SEARCH = 5 * TileSize
    // Close enough to have landed. Small, because the approach steps straight in and clamps to the
    // remaining distance -- there is no "close enough" jump to hide.
    private static readonly PERCH_ARRIVE = 0.05
    // How long it sits, before the gust makes it sit longer.
    private static readonly PERCH_DWELL_MS = [2200, 7000]
    // ...and how long it flies before it thinks about landing again.
    private static readonly FLIGHT_MS = [2500, 9000]
    // The lazy open-and-close of a settled butterfly's wings, in radians/sec. A deliberate exception to
    // the beat being derived from travel: a perched one is not travelling at all, so the derived beat
    // would be zero and the wings would simply freeze wherever they stopped.
    private static readonly PERCHED_FLAP_RATE = 2.2

    private static readonly TELEPORT_JUMP = Butterflies.HALF_W

    private butterflies: Butterfly[] = []
    private activeCount = 0
    private textures: Texture[] = []
    private running = false
    private lastMs = 0
    private lastCenterX = 0
    private lastCenterY = 0
    private hasCenter = false

    /**
     * `random` is every roll this effect makes -- the scatter, each butterfly's speed, scale, tint
     * and wingbeat, how long it flies before looking for a leaf, how long it sits, and above all the
     * heading target it re-rolls to make the path wander. It defaults to Math.random, so the game
     * reads exactly as before; it is injected so a test can hand over a SEEDED stream and assert on
     * the flight it produces. Without that the flight is unreproducible, and an assertion over one
     * butterfly's few seconds of wandering then passes or fails on the draw -- which is precisely
     * how "wanders instead of flying in a straight line" came to fail about one run in twenty-five.
     */
    constructor(
        private makeTextures: () => Texture[],
        private findPerch?: PerchFinder,
        private random: () => number = Math.random,
    ) {
        const container = new Container()
        container.zIndex = Butterflies.Z_INDEX
        container.interactiveChildren = false
        container.visible = false
        this.container = container
    }

    // A value from a [min, max] range. An instance method rather than a static one so it draws from
    // the injected stream like everything else here.
    private pick = ([min, max]: number[]) => min + this.random() * (max - min)

    // Exposed for tests: how many are currently flying.
    getActiveCount = () => (this.running ? this.activeCount : 0)

    // Exposed for tests: how many are currently sitting on something.
    getPerchedCount = () =>
        this.running ? this.butterflies.slice(0, this.activeCount).filter(b => b.state === "perched").length : 0

    /**
     * Fly them for one frame. `brightness` is the client's interpolated daylight, or undefined in a
     * room with no day cycle (which counts as day). Cheap to call every frame in any room.
     */
    update = (config: ButterfliesConfig | null, center?: Coordinate, brightness?: number) => {
        const resolved = this.resolve(config, brightness)
        if (!resolved || !center) {
            this.stop()
            return
        }

        const nowMs = performance.now()
        if (!this.running) {
            this.running = true
            this.container.visible = true
            this.lastMs = nowMs
            this.ensurePool(resolved.count, center.x, center.y, resolved, nowMs)
            this.scatter(center.x, center.y, resolved, nowMs)
            this.lastCenterX = center.x
            this.lastCenterY = center.y
            this.hasCenter = true
            return
        }

        this.ensurePool(resolved.count, center.x, center.y, resolved, nowMs)

        if (
            this.hasCenter &&
            (Math.abs(center.x - this.lastCenterX) > Butterflies.TELEPORT_JUMP ||
                Math.abs(center.y - this.lastCenterY) > Butterflies.TELEPORT_JUMP)
        ) {
            this.scatter(center.x, center.y, resolved, nowMs)
            this.lastMs = nowMs
            this.lastCenterX = center.x
            this.lastCenterY = center.y
            return
        }
        this.lastCenterX = center.x
        this.lastCenterY = center.y
        this.hasCenter = true

        let dt = (nowMs - this.lastMs) / 1000
        this.lastMs = nowMs
        if (!(dt > 0)) {
            return
        }
        // Clamped for the same reason as the leaves: a tab returning from the background would
        // otherwise advance every butterfly by minutes in one step.
        dt = Math.min(dt, 0.05)

        this.step(dt, nowMs, center.x, center.y, resolved)
    }

    private stop = () => {
        if (!this.running) {
            return
        }
        this.running = false
        this.container.visible = false
        this.lastMs = 0
        this.hasCenter = false
    }

    private resolve = (config: ButterfliesConfig | null, brightness?: number) => {
        const w = window as any
        const forced = typeof w.butterflies === "boolean" ? (w.butterflies as boolean) : undefined
        const enabled = forced !== undefined ? forced : !!config?.enabled
        if (!enabled) {
            return null
        }

        const num = (override: any, value: number | undefined, fallback: number) =>
            typeof override === "number" ? override : typeof value === "number" ? value : fallback

        // Forcing them on from the console lifts the day gate too -- otherwise there is no way to look
        // at them except by waiting for the right hour.
        const dayOnly = forced === true ? false : config?.dayOnly !== false
        const day = dayOnly ? daylightFraction(brightness) : 1
        if (day <= 0) {
            return null
        }

        // The shared wind, if this room has any. A gust is the one thing that stops a butterfly flying,
        // so its strength is read here and spent on speed below. A room with no wind is simply calm.
        const wind = windAt(null, performance.now() / 1000)
        const gust = wind ? wind.gust : 1
        // Sheltering: brisk in a lull, much slower at the top of a gust. Floored well above zero --
        // because the wingbeat follows the speed, a butterfly slowed to a crawl would also stop
        // flapping, which reads as one dying rather than one waiting out a gust.
        const shelter = Math.min(1, Math.max(0.45, 1.3 - gust * 0.5))

        const scaleMin = config?.scaleMin ?? Butterflies.DEFAULT_SCALE_MIN
        const scaleMax = config?.scaleMax ?? Butterflies.DEFAULT_SCALE_MAX

        return {
            // Fewer about as the light goes, and none once it is properly dark.
            count: Math.max(
                day > 0 ? 1 : 0,
                Math.min(
                    Butterflies.MAX_BUTTERFLIES,
                    Math.round(num(w.butterflyCount, config?.count, Butterflies.DEFAULT_COUNT) * day),
                ),
            ),
            speed: Math.max(1, num(w.butterflySpeed, config?.speed, Butterflies.DEFAULT_SPEED)) * shelter,
            // Sinking a little in the gust as well as slowing, which is what sheltering looks like.
            bobScale: shelter,
            alpha: Math.min(1, Math.max(0, num(w.butterflyAlpha, config?.alpha, Butterflies.DEFAULT_ALPHA))) * day,
            scaleMin: Math.max(0.05, Math.min(scaleMin, scaleMax)),
            scaleMax: Math.max(scaleMin, scaleMax),
            colors: config?.colors?.length ? config.colors : Butterflies.DEFAULT_COLORS,
        }
    }

    private step = (
        dt: number,
        nowMs: number,
        centerX: number,
        centerY: number,
        cfg: NonNullable<ReturnType<Butterflies["resolve"]>>,
    ) => {
        const perching = (window as any).butterflyPerch !== false
        const homeW = Butterflies.HALF_W * Butterflies.HOME_FRACTION
        const homeH = Butterflies.HALF_H * Butterflies.HOME_FRACTION

        for (let i = 0; i < this.activeCount; i++) {
            const b = this.butterflies[i]

            // PERCHED: sitting still, wings opening and closing lazily, until its time is up.
            if (b.state === "perched") {
                b.flapPhase += Butterflies.PERCHED_FLAP_RATE * dt
                const sprite = b.sprite
                sprite.x = b.x
                sprite.y = b.y
                // Wings held mostly open and moved gently, rather than folding right down: a settled
                // butterfly fans them, it does not beat them.
                const fan = 0.45 + 0.55 * Math.abs(Math.cos(b.flapPhase))
                sprite.rotation = b.heading + Math.PI / 2
                sprite.scale.set(b.scale * fan, b.scale)
                sprite.alpha = b.baseAlpha * cfg.alpha
                if (nowMs >= b.stateUntilMs) {
                    b.state = "flying"
                    b.heading = this.random() * Math.PI * 2
                    b.headingTarget = b.heading
                    b.nextTurnMs = nowMs + 300
                    b.stateUntilMs = nowMs + this.pick(Butterflies.FLIGHT_MS)
                }
                continue
            }

            // FLYING, and long enough since the last rest to think about landing. A gust makes it
            // readier to settle -- being blown about is what sends one looking for a leaf.
            if (b.state === "flying" && nowMs >= b.stateUntilMs && this.findPerch && perching) {
                const spot = this.findPerch(b.x, b.y, Butterflies.PERCH_SEARCH)
                if (spot) {
                    b.state = "approaching"
                    b.perchX = spot.x
                    b.perchY = spot.y
                } else {
                    // Nowhere to land here; try again shortly rather than every frame.
                    b.stateUntilMs = nowMs + 1500
                }
            }

            // Pick somewhere new to be heading, every second or so. This is the wander.
            if (b.state === "flying" && nowMs >= b.nextTurnMs) {
                b.headingTarget = b.heading + (this.random() * 2 - 1) * 1.6
                b.nextTurnMs = nowMs + 500 + this.random() * 1600
            }

            // Past the inner boundary, the next decision is made for it: turn back towards the middle.
            // Steering rather than teleporting is the whole difference between milling about a meadow
            // and being blown across one.
            const outX = b.x - centerX
            const outY = b.y - centerY
            // Stranded well outside the box -- however it got there -- is not something to steer back
            // from, because at twenty pixels a second it would take the best part of a minute. Put it
            // back. Nothing should reach this now that the pool places what it creates; it is here so
            // that if anything ever does, it self-corrects rather than going missing.
            if (Math.abs(outX) > Butterflies.HALF_W * 2 || Math.abs(outY) > Butterflies.HALF_H * 2) {
                this.place(b, centerX, centerY, cfg, nowMs)
                continue
            }
            if (Math.abs(outX) > homeW || Math.abs(outY) > homeH) {
                b.headingTarget = Math.atan2(-outY, -outX) + (this.random() * 2 - 1) * 0.5
            }

            // APPROACHING: go straight at the spot rather than steering onto it.
            //
            // Two reasons it is handled here instead of by the heading, which was the first attempt.
            // A butterfly whose turn rate cannot keep up ORBITS the spot forever, circling something a
            // few pixels away; and arriving "close enough" and then snapping the last couple of pixels
            // is a jump of a whole body length at this size. Stepping straight in, clamped so it
            // cannot overshoot, lands exactly on the spot and cannot circle it.
            if (b.state === "approaching") {
                const dx = b.perchX - b.x
                const dy = b.perchY - b.y
                const distance = Math.hypot(dx, dy) || 1
                // Ease off, but LATE and not by much. A butterfly does not glide in on a long approach;
                // it flies at the flower at pace and drops onto it over the last few body-lengths. This
                // holds full speed until about thirteen pixels out -- three or four of its own
                // wingspans -- and then eases to half. An earlier, deeper ramp (from six tiles out,
                // down to a fifth of speed) read as swimming towards the leaf.
                //
                // The wingbeat follows travel speed, so this is also what makes it settle rather than
                // stop dead: the beat drops with the approach on its own.
                const closing = Math.min(1, 0.5 + distance / (0.8 * TileSize))
                const travel = b.speed * cfg.speed * closing
                const stepLength = Math.min(distance, travel * dt)

                b.x += (dx / distance) * stepLength
                b.y += (dy / distance) * stepLength
                b.heading = Math.atan2(dy, dx)
                b.headingTarget = b.heading
                b.flapPhase += b.flapRate * (travel / Butterflies.FLAP_REFERENCE) * dt

                if (distance - stepLength <= Butterflies.PERCH_ARRIVE) {
                    b.x = b.perchX
                    b.y = b.perchY
                    b.state = "perched"
                    b.stateUntilMs = nowMs + this.pick(Butterflies.PERCH_DWELL_MS) * (2 - cfg.bobScale)
                }
                this.draw(b, cfg, 0)
                continue
            }

            // Ease the heading towards the target so a turn is a curve, and take the SHORT way round --
            // without this a butterfly turning past due west unwinds the long way, a full circle.
            let delta = b.headingTarget - b.heading
            while (delta > Math.PI) delta -= Math.PI * 2
            while (delta < -Math.PI) delta += Math.PI * 2
            b.heading += delta * Math.min(1, dt * 3.5)

            // How fast this one is actually travelling right now, after its own variation and after
            // sheltering from the wind.
            const travel = b.speed * cfg.speed
            b.x += Math.cos(b.heading) * travel * dt
            b.y += Math.sin(b.heading) * travel * dt

            // The wingbeat is DERIVED from that, so distance covered per beat stays roughly constant
            // whatever the speed. Set the two independently and they disagree the moment either one
            // changes -- which is exactly how this looked first: beating hard and going nowhere.
            b.flapPhase += b.flapRate * (travel / Butterflies.FLAP_REFERENCE) * dt

            this.draw(b, cfg, cfg.bobScale)
        }
    }

    /**
     * Put one airborne butterfly where its numbers say it is.
     *
     * `bobStrength` is zero while it is landing: the body bobs between wingbeats in flight, but a
     * butterfly coming in to settle should not be bouncing as it touches down.
     */
    private draw = (b: Butterfly, cfg: NonNullable<ReturnType<Butterflies["resolve"]>>, bobStrength: number) => {
        const sprite = b.sprite
        // The wings fold almost shut and spring open twice per bob -- abs() of a cosine gives the
        // sharp turn at the moment they are closed, which a plain sine would round off into a pulse.
        // This is the single cue that says "butterfly" at this size.
        const wing = 0.18 + 0.82 * Math.abs(Math.cos(b.flapPhase))
        // ...and the body rises once per cycle, off the same phase.
        const lift = Math.sin(b.flapPhase) * b.bob * bobStrength

        sprite.x = b.x
        sprite.y = b.y + lift
        // The art is drawn with the body along -y, so this turns the body to face the heading.
        sprite.rotation = b.heading + Math.PI / 2
        sprite.scale.set(b.scale * wing, b.scale)
        sprite.alpha = b.baseAlpha * cfg.alpha
    }

    /**
     * Grow the pool to `count`, placing anything it creates around the player.
     *
     * The placing is the point. This is NOT only called on the first frame: the count scales with the
     * daylight, so butterflies are added one at a time as dawn comes up. A new one that was merely
     * constructed would sit at the world origin on heading zero and then fly towards the player from
     * wherever that happens to be -- for as long as it took. The leaves get away with a bare pool
     * because a leaf outside its box is reseeded on the next frame; a butterfly is steered rather than
     * recycled, so nothing would ever come along and correct it.
     */
    private ensurePool = (
        count: number,
        centerX: number,
        centerY: number,
        cfg: NonNullable<ReturnType<Butterflies["resolve"]>>,
        nowMs: number,
    ) => {
        const target = Math.min(count, Butterflies.MAX_BUTTERFLIES)
        if (!this.textures.length && target > 0) {
            try {
                this.textures = this.makeTextures()
            } catch (e) {
                this.textures = []
            }
            if (!this.textures.length) {
                this.textures = [Texture.WHITE]
            }
        }
        while (this.butterflies.length < target) {
            const sprite = new Sprite(this.textures[this.butterflies.length % this.textures.length] ?? Texture.WHITE)
            sprite.anchor.set(0.5)
            this.container.addChild(sprite)
            const butterfly: Butterfly = {
                sprite,
                x: 0,
                y: 0,
                heading: 0,
                headingTarget: 0,
                nextTurnMs: 0,
                speed: 1,
                flapPhase: 0,
                flapRate: 1,
                bob: 1,
                scale: 0.4,
                baseAlpha: 1,
                state: "flying",
                perchX: 0,
                perchY: 0,
                stateUntilMs: 0,
            }
            this.butterflies.push(butterfly)
            this.place(butterfly, centerX, centerY, cfg, nowMs)
        }
        if (target === this.activeCount) {
            return
        }
        for (let i = 0; i < this.butterflies.length; i++) {
            this.butterflies[i].sprite.visible = i < target
        }
        this.activeCount = target
    }

    private scatter = (
        centerX: number,
        centerY: number,
        cfg: NonNullable<ReturnType<Butterflies["resolve"]>>,
        nowMs: number,
    ) => {
        for (let i = 0; i < this.activeCount; i++) {
            this.place(this.butterflies[i], centerX, centerY, cfg, nowMs)
        }
    }

    // Give one butterfly fresh flight characteristics and put it somewhere near the player. Placed
    // inside the home boundary rather than the whole box, so none of them starts already turning back.
    private place = (
        b: Butterfly,
        centerX: number,
        centerY: number,
        cfg: NonNullable<ReturnType<Butterflies["resolve"]>>,
        nowMs: number,
    ) => {
        this.randomize(b, cfg, nowMs)
        b.x = centerX + (this.random() * 2 - 1) * Butterflies.HALF_W * Butterflies.HOME_FRACTION
        b.y = centerY + (this.random() * 2 - 1) * Butterflies.HALF_H * Butterflies.HOME_FRACTION
        b.sprite.x = b.x
        b.sprite.y = b.y
    }

    private randomize = (b: Butterfly, cfg: NonNullable<ReturnType<Butterflies["resolve"]>>, nowMs: number) => {
        const rand = (min: number, max: number) => min + this.random() * (max - min)
        b.heading = this.random() * Math.PI * 2
        b.headingTarget = b.heading
        b.nextTurnMs = nowMs + this.random() * 1200
        // Always airborne to begin with, and never still believing it is mid-landing: this also runs
        // when one is re-placed after a teleport or when the pool grows, and a stale perch target
        // would send it back across the room to a tuft of grass that is no longer there.
        b.state = "flying"
        b.perchX = 0
        b.perchY = 0
        b.stateUntilMs = nowMs + this.pick(Butterflies.FLIGHT_MS)
        b.speed = rand(0.7, 1.35)
        // Six to eight VISIBLE flaps a second at the reference speed, scaled from there by how fast
        // this one is actually going.
        //
        // Note the factor of pi rather than 2pi: the wings fold on abs(cos), which closes them TWICE
        // per phase cycle, so a phase rate of 2pi*f gives 2f flaps. Quoting this in cycles instead of
        // flaps is how it first ended up at 12-16 a second -- a bee's buzz, where a butterfly's flap is
        // slow and floppy and is half the reason you can tell one from the other across a field.
        b.flapRate = rand(Math.PI * 6, Math.PI * 8)
        b.bob = rand(1.1, 2.4)
        b.scale = rand(cfg.scaleMin, cfg.scaleMax)
        b.baseAlpha = rand(0.85, 1)
        b.sprite.texture = this.textures[Math.floor(this.random() * this.textures.length)] ?? Texture.WHITE
        b.sprite.tint = cfg.colors[Math.floor(this.random() * cfg.colors.length)]
    }

    /**
     * The butterfly art, generated once rather than shipped: a body along -y with a fore and hind wing
     * either side, drawn white so a single texture serves the whole palette by tint.
     *
     * The body runs along the sprite's Y axis so that squashing X folds the wings towards it -- get
     * that the other way round and the flap squashes the butterfly along its length instead, which
     * reads as a bug being stepped on rather than one in flight.
     */
    static buildButterflyTextures = (generate: (g: Graphics) => Texture): Texture[] => {
        // [forewing radius, hindwing radius, body length]. Two shapes is enough variety at this size.
        const shapes: Array<[number, number, number]> = [
            [5, 3.6, 9],
            [4.2, 3, 7.5],
        ]
        return shapes.map(([fore, hind, body]) => {
            const g = new Graphics()
            g.beginFill(0xffffff)
            // Forewings, set slightly forward of centre, and the smaller hindwings behind them.
            g.drawEllipse(-fore * 0.75, -body * 0.18, fore, fore * 0.78)
            g.drawEllipse(fore * 0.75, -body * 0.18, fore, fore * 0.78)
            g.drawEllipse(-hind * 0.8, body * 0.25, hind, hind * 0.85)
            g.drawEllipse(hind * 0.8, body * 0.25, hind, hind * 0.85)
            g.endFill()
            // The body, darker so it survives tinting as a visible seam between the wings -- without it
            // the two halves merge into one blob the moment the wings fold.
            g.beginFill(0x6b6b6b)
            g.drawEllipse(0, 0, body * 0.11, body * 0.5)
            g.endFill()
            const texture = generate(g)
            g.destroy()
            return texture
        })
    }

    // The default texture factory, bound to a live renderer. Split out so the flight can be tested
    // without a GPU.
    static textureFactory = (renderer: { generateTexture: (g: Graphics, options?: any) => Texture }) => (): Texture[] =>
        Butterflies.buildButterflyTextures(g =>
            renderer.generateTexture(g, { resolution: 2, scaleMode: SCALE_MODES.LINEAR }),
        )
}
